import React, { useEffect, useMemo, useState, useRef } from 'react';
import FollowButton from './FollowButton';
import { Link, useNavigate } from 'react-router-dom';
import { auth, db, storage, functions } from '../firebase';
import { collection, collectionGroup, doc, getDocs, onSnapshot, query, where, limit, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import { observeWalletBalance } from '../services/transactionsService';
import ArtistUpload from './ArtistUpload';
import FollowersFollowingModal from './FollowersFollowingModal';
import { createSongPostNotifications } from '../services/notificationService';
import { getUserById } from '../services/userService';

const BADGE_LEVELS = [
    { type: 'bronze', minimumTips: 1 },
    { type: 'silver', minimumTips: 3 },
    { type: 'gold', minimumTips: 5 },
    { type: 'diamond', minimumTips: 10 },
    { type: 'crown', minimumTips: 20 },
];

const BADGE_EMOJIS = {
    bronze: '🥉',
    silver: '🥈',
    gold: '🥇',
    diamond: '💎',
    crown: '👑',
};

const ArtistProfile = ({ uid }) => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [songs, setSongs] = useState([]);
    const [artistBadges, setArtistBadges] = useState([]);
    const [artistBadgeCounts, setArtistBadgeCounts] = useState({
        crown: 0,
        diamond: 0,
        gold: 0,
        silver: 0,
        bronze: 0,
    });
    const [recentReceivedTips, setRecentReceivedTips] = useState([]);
    const [walletBalance, setWalletBalance] = useState(0);
    const [walletGlow, setWalletGlow] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [showFollowersModal, setShowFollowersModal] = useState(false);
    const [showFollowingModal, setShowFollowingModal] = useState(false);
    const [selectedBadgeType, setSelectedBadgeType] = useState(null);
    const [selectedBadgeFans, setSelectedBadgeFans] = useState([]);
    const [loadingBadgeFans, setLoadingBadgeFans] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [songName, setSongName] = useState('');
    const [confirmedUpload, setConfirmedUpload] = useState(false);
    const [showDetailsPage, setShowDetailsPage] = useState(false);
    const fileInputRef = useRef(null);
    const isOwnProfile = useMemo(() => !!auth.currentUser && !!uid && auth.currentUser.uid === uid, [uid]);
    const [followersCount, setFollowersCount] = useState(0);
    const [followingCount, setFollowingCount] = useState(0);
    const [currentAuthUserId, setCurrentAuthUserId] = useState(null);

    const openBadgeFans = async (badgeType) => {
        setSelectedBadgeType(badgeType);
        setLoadingBadgeFans(true);
        try {
            const snapshot = await getDocs(collection(db, 'artistBadges', uid, 'fans'));
            const matchingFans = snapshot.docs.filter((fanDoc) => (
                Number(fanDoc.data()?.tipCount || 0) >= (
                    BADGE_LEVELS.find((level) => level.type === badgeType)?.minimumTips || 1
                )
            ));
            const fans = await Promise.all(matchingFans.map(async (fanDoc) => {
                const fanData = fanDoc.data() || {};
                const listenerId = fanData.userId || fanDoc.id;
                const listener = await getUserById(listenerId);
                return {
                    id: listenerId,
                    name: listener?.name || listener?.displayName || listener?.email || 'Listener',
                    photoURL: listener?.photoURL || listener?.photoUrl || null,
                    role: listener?.role || 'listener',
                    tipCount: Number(fanData.tipCount || 0),
                };
            }));
            fans.sort((a, b) => b.tipCount - a.tipCount || a.name.localeCompare(b.name));
            setSelectedBadgeFans(fans);
        } catch (error) {
            console.error('Failed to load badge fans:', error);
            setSelectedBadgeFans([]);
        } finally {
            setLoadingBadgeFans(false);
        }
    };

    // Monitor auth state changes to ensure we always have the latest user
    useEffect(() => {
        // Get initial auth state
        if (auth.currentUser) {
            setCurrentAuthUserId(auth.currentUser.uid);
            console.log('🔐 ArtistProfile - Initial auth user:', auth.currentUser.uid);
        }

        // Subscribe to auth state changes
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setCurrentAuthUserId(user.uid);
                console.log('🔐 ArtistProfile - Auth state changed, current user:', user.uid);
            } else {
                setCurrentAuthUserId(null);
                console.log('🔐 ArtistProfile - No user logged in');
            }
        });

        return () => unsubscribe();
    }, []);

    // Listen for upload modal event from ProfilePage
    useEffect(() => {
        const handler = () => {
            // Directly trigger file input click instead of opening modal
            if (fileInputRef.current) {
                fileInputRef.current.click();
            }
        };
        window.addEventListener('open-upload-modal', handler);
        return () => window.removeEventListener('open-upload-modal', handler);
    }, []);

    // Validate audio file
    const validateAudioFile = (file) => {
        const ALLOWED_FORMATS = {
            extensions: ['.mp3', '.aac', '.m4a', '.wav', '.flac', '.ogg', '.opus', '.aiff'],
            mimeTypes: [
                'audio/mpeg',
                'audio/mp4',
                'audio/x-m4a',
                'audio/wav',
                'audio/flac',
                'audio/ogg',
                'audio/opus',
                'audio/aiff',
                'audio/wave',
                'audio/x-wav'
            ]
        };

        // Blocked video formats - explicitly reject video files
        const BLOCKED_VIDEO_FORMATS = {
            extensions: ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.flv', '.wmv', '.m4v', '.3gp', '.mpg', '.mpeg', '.m2v'],
            mimeTypes: [
                'video/mp4',
                'video/quicktime',
                'video/x-msvideo',
                'video/x-matroska',
                'video/webm',
                'video/x-flv',
                'video/x-ms-wmv',
                'video/3gpp',
                'video/mpeg',
                'video/x-m4v'
            ]
        };

        // First check if it's a video file - reject immediately
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        const isVideoExtension = BLOCKED_VIDEO_FORMATS.extensions.includes(fileExtension);
        const isVideoMimeType = BLOCKED_VIDEO_FORMATS.mimeTypes.includes(file.type) || file.type.startsWith('video/');

        if (isVideoExtension || isVideoMimeType) {
            return {
                isValid: false,
                errors: {
                    isVideo: true,
                    extension: false,
                    mimeType: false,
                    size: false
                }
            };
        }

        // Check if it's a valid audio file
        const isValidExtension = ALLOWED_FORMATS.extensions.includes(fileExtension);
        const isValidMimeType = ALLOWED_FORMATS.mimeTypes.includes(file.type);
        const isValidSize = file.size <= 100 * 1024 * 1024; // 100MB

        return {
            isValid: isValidExtension && isValidMimeType && isValidSize,
            errors: {
                isVideo: false,
                extension: !isValidExtension,
                mimeType: !isValidMimeType,
                size: !isValidSize
            }
        };
    };

    // Handle file selection and upload
    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Check internet connection first
        if (!navigator.onLine) {
            alert('📡 No Internet Connection\n\nPlease check your internet connection and try again.');
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        // Check if user is authenticated
        if (!auth.currentUser) {
            alert('❌ You must be logged in to upload songs. Please sign in and try again.');
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        // Verify uid matches current user
        if (auth.currentUser.uid !== uid) {
            alert('❌ Authentication error. Please refresh the page and try again.');
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        // Validate file
        const validation = validateAudioFile(file);
        if (!validation.isValid) {
            if (validation.errors.isVideo) {
                alert('❌ Video files are not allowed! Please upload only audio files (MP3, M4A, WAV, FLAC, OGG, etc.)');
            } else if (validation.errors.extension || validation.errors.mimeType) {
                alert('❌ Only standard audio formats allowed (MP3, M4A, WAV, FLAC, OGG, etc.). Video files are not allowed.');
            } else if (validation.errors.size) {
                alert('❌ File size must be less than 100MB');
            }
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        // Store file and show details page
        setSelectedFile(file);
        const fileNameWithoutExt = file.name.replace(/\.(mp3|aac|m4a|wav|flac|ogg|opus|aiff)$/i, '');
        setSongName(fileNameWithoutExt);
        setShowDetailsPage(true);
        setUploadError(null);
        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleUploadFromDetails = async () => {
        if (!confirmedUpload) {
            setUploadError('Please confirm that you have the rights and permissions to upload this music.');
            return;
        }
        if (!selectedFile || !songName.trim()) {
            setUploadError('Select an audio file and enter a song name before uploading.');
            return;
        }

        const file = selectedFile;
        // Upload file
        try {
            setUploading(true);
            setUploadError(null);
            
            // Sanitize file name - remove special characters and spaces
            const sanitizeFileName = (fileName) => {
                // Get file extension
                const ext = fileName.substring(fileName.lastIndexOf('.'));
                // Get file name without extension
                const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
                // Replace special characters with underscores and remove spaces
                const sanitized = nameWithoutExt
                    .replace(/[^a-zA-Z0-9]/g, '_')
                    .replace(/_+/g, '_')
                    .replace(/^_|_$/g, '');
                return sanitized + ext;
            };
            
            // Use logged-in user's ID for storage path, not profile uid
            const uploaderIdForStorage = auth.currentUser?.uid;
            if (!uploaderIdForStorage) {
                throw new Error('❌ You must be logged in to upload songs.');
            }
            
            const sanitizedFileName = sanitizeFileName(file.name);
            const path = `songs/${uploaderIdForStorage}/${Date.now()}_${sanitizedFileName}`;
            console.log('Uploading song to path:', path, '| uploaderId:', uploaderIdForStorage);
            const storageRef = ref(storage, path);
            
            // Use resumable upload for better reliability and progress tracking capability
            const uploadTask = uploadBytesResumable(storageRef, file);
            
            // Wait for upload to complete with longer timeout for better reliability
            const UPLOAD_TIMEOUT = 600000; // 10 minutes timeout for songs (allows for large files and slow connections)
            let uploadCompleted = false;
            let timeoutId = null;
            
            const fileURL = await new Promise((resolve, reject) => {
                // Set timeout to prevent hanging - but don't cancel immediately
                timeoutId = setTimeout(() => {
                    if (!uploadCompleted) {
                        // Check if upload is still in progress
                        const taskState = uploadTask.snapshot?.state;
                        if (taskState === 'running' || taskState === 'paused') {
                            // Upload is still active, give it more time
                            console.warn('Upload taking longer than expected, but still in progress...');
                            // Reset timeout for another 5 minutes
                            timeoutId = setTimeout(() => {
                                if (!uploadCompleted) {
                                    uploadTask.cancel();
                                    reject(new Error('Upload timeout: The upload took too long. Please check your internet connection and try again with a smaller file.'));
                                }
                            }, 300000); // Additional 5 minutes
                        } else {
                            // Upload seems stuck, cancel it
                            uploadTask.cancel();
                            reject(new Error('Upload timeout: Please check your internet connection and try again.'));
                        }
                    }
                }, UPLOAD_TIMEOUT);
                
                uploadTask.on(
                    'state_changed',
                    (snapshot) => {
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        setUploadProgress(Math.round(progress));
                        console.log(`Upload progress: ${Math.round(progress)}% (${(snapshot.bytesTransferred / 1024 / 1024).toFixed(2)} MB / ${(snapshot.totalBytes / 1024 / 1024).toFixed(2)} MB)`);
                        
                        // Reset timeout on progress - upload is active
                        if (timeoutId) {
                            clearTimeout(timeoutId);
                            timeoutId = setTimeout(() => {
                                if (!uploadCompleted) {
                                    uploadTask.cancel();
                                    reject(new Error('Upload timeout: Please check your internet connection and try again.'));
                                }
                            }, UPLOAD_TIMEOUT);
                        }
                    },
                    (error) => {
                        if (timeoutId) clearTimeout(timeoutId);
                        console.error('Upload error:', error);
                        console.error('Error code:', error.code);
                        console.error('Error message:', error.message);
                        
                        // Don't reject if it's a cancellation we initiated
                        if (error.code === 'storage/canceled' && uploadCompleted) {
                            return; // Already handled
                        }
                        
                        // Check if offline
                        if (!navigator.onLine) {
                            reject(new Error('📡 No Internet Connection\n\nPlease check your internet connection and try again.'));
                            return;
                        }
                        
                        let errorMessage = 'Upload failed. ';
                        if (error.code === 'storage/unauthorized') {
                            errorMessage += 'Permission denied. Please check Firebase Storage rules.';
                        } else if (error.code === 'storage/canceled') {
                            // Only show cancellation error if it wasn't due to timeout
                            if (!errorMessage.includes('timeout')) {
                                errorMessage += 'Upload was canceled.';
                            }
                        } else if (error.code === 'storage/unknown' || error.code === 'storage/retry-limit-exceeded') {
                            errorMessage += '📡 Network error. Please check your internet connection and try again.';
                        } else if (error.message && (error.message.includes('ERR_FAILED') || error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.message.includes('CORS'))) {
                            errorMessage += '📡 Network request failed. Please check your internet connection and try again.';
                        } else {
                            errorMessage += error.message || 'Please try again.';
                        }
                        reject(new Error(errorMessage));
                    },
                    async () => {
                        // Upload completed successfully
                        try {
                            if (timeoutId) clearTimeout(timeoutId);
                            uploadCompleted = true;
                            console.log('Getting download URL...');
                            const url = await getDownloadURL(uploadTask.snapshot.ref);
                            console.log('✅ Download URL obtained:', url);
                            resolve(url);
                        } catch (err) {
                            if (timeoutId) clearTimeout(timeoutId);
                            console.error('Error getting download URL:', err);
                            reject(new Error('Failed to get download URL: ' + (err.message || 'Unknown error')));
                        }
                    }
                );
            });

            // Create song document in Firestore
            const songTitle = songName.trim() || file.name.replace(/\.(mp3|aac|m4a|wav|flac|ogg|opus|aiff)$/i, '');
            
            // ALWAYS use logged-in user's ID for uploads - song belongs to the user who uploads it
            // Force refresh auth token to ensure we have the latest user
            try {
                if (auth.currentUser) {
                    await auth.currentUser.getIdToken(true);
                }
            } catch (tokenErr) {
                console.warn('Could not refresh auth token:', tokenErr);
            }
            
            await auth.authStateReady?.(); // forces firebase auth to be ready

            const finalUploaderId = auth.currentUser?.uid;

            if (!finalUploaderId) {
                alert("You are not logged in yet. Please wait and try again.");
                return;
            }
            
            // Verify that logged-in user matches the profile being viewed (security check)
            if (finalUploaderId !== uid) {
                throw new Error('❌ Authentication error: You can only upload songs to your own profile.');
            }
            
            const songData = {
                title: songTitle,
                songUrl: fileURL,
                artistId: finalUploaderId,    // Use uploader's ID directly
                userId: finalUploaderId,      // Also save as userId for consistency
                ownerId: finalUploaderId,     // Also save as ownerId for compatibility
                artistUID: finalUploaderId,   // Also save as artistUID for compatibility
                artistName: user?.name || user?.displayName || 'Unknown Artist',
                genre: 'Unknown',
                uploadDate: new Date(),
                tipsTotal: 0,
                tipsCount: 0,
                plays: 0,
                playCount: 0,
                format: file.name.split('.').pop().toLowerCase(),
                size: file.size,
                mimeType: file.type
            };
            
            console.log('📝 Song document data being saved:', {
                artistId: songData.artistId,
                userId: songData.userId,
                ownerId: songData.ownerId,
                artistUID: songData.artistUID,
                title: songData.title
            });
            
            const createSong = httpsCallable(functions, 'createSong');
            await createSong({ rightsConfirmation: confirmedUpload, song: songData });
            
            console.log('✅ Song document created successfully with artistId/userId/ownerId/artistUID:', finalUploaderId);

            // Update user document - use finalUploaderId, not uid prop (for consistency)
            const userRef = doc(db, 'users', finalUploaderId);
            await updateDoc(userRef, {
                hasUploadedSong: true,
                uploadedAt: new Date(),
                posts: increment(1)
            });

            // Create notifications for followers - use finalUploaderId
            createSongPostNotifications(finalUploaderId, songTitle).catch(err => {
                console.error('Failed to create song post notifications:', err);
            });

            console.log('✅ Song uploaded successfully!');
            alert('✅ Song uploaded successfully!');
            setUploadError(null);
            
            // Songs list will auto-update via onSnapshot listener
            // No need to reload the page
        } catch (error) {
            console.error('Upload error:', error);
            // Check if offline
            if (!navigator.onLine) {
                setUploadError('📡 No Internet Connection\n\nPlease check your internet connection and try again.');
                alert('📡 No Internet Connection\n\nPlease check your internet connection and try again.');
            } else {
                const errorMsg = error.message || 'Upload failed. Please try again.';
                setUploadError(errorMsg);
                alert(`❌ ${errorMsg}`);
            }
        } finally {
            setUploading(false);
            setUploadProgress(0);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Realtime user doc listener - render immediately, update as data loads
    useEffect(() => {
        if (!uid) {
            setUser(null);
            return () => {};
        }
        const uRef = doc(db, 'users', uid);
        const unsub = onSnapshot(uRef, (snap) => {
            if (snap.exists()) {
                const userData = { id: snap.id, ...snap.data() };
                setUser(userData);
            } else {
                setUser(null);
            }
        }, (err) => {
            console.error('User snapshot error', err);
            setUser(null);
        });
        return () => unsub();
    }, [uid]);

    // Load songs for this artist with real-time updates
    useEffect(() => {
        if (!uid) {
            setSongs([]);
            return () => {};
        }
        
        const q = query(
            collection(db, 'songs'),
            where('artistId', '==', uid)
        );
        
        const unsub = onSnapshot(
            q,
            async (snap) => {
                const list = snap.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .sort((a, b) => {
                        const getTime = (song) => {
                            const stamp = song.uploadDate || song.createdAt || song.timestamp;
                            if (stamp?.toMillis) return stamp.toMillis();
                            if (stamp?.seconds) return stamp.seconds * 1000;
                            const parsed = Date.parse(stamp || '');
                            return Number.isNaN(parsed) ? 0 : parsed;
                        };
                        return getTime(b) - getTime(a);
                    });
                setSongs(list);
            },
            (err) => {
                console.error('Failed to load songs for artist', err);
            }
        );
        
        return () => unsub();
    }, [uid]);

    // Keep the artist badge summary live after each webhook-confirmed tip.
    useEffect(() => {
        if (!uid) return () => {};
        const fansRef = collection(db, 'artistBadges', uid, 'fans');
        const unsub = onSnapshot(fansRef, (snap) => {
            const counts = { crown: 0, diamond: 0, gold: 0, silver: 0, bronze: 0 };
            const titles = [];
            snap.docs.forEach((fanDoc) => {
                const data = fanDoc.data() || {};
                const tipCount = Number(data.tipCount || 0);
                BADGE_LEVELS.forEach(({ type, minimumTips }) => {
                    if (tipCount >= minimumTips) counts[type] += 1;
                });
                if (data.badgeTitle) titles.push(data.badgeTitle);
            });
            setArtistBadges(titles);
            setArtistBadgeCounts(counts);
        }, (err) => {
            console.error('Artist badge summary snapshot error', err);
            setArtistBadges([]);
            setArtistBadgeCounts({ crown: 0, diamond: 0, gold: 0, silver: 0, bronze: 0 });
        });
        return () => unsub();
    }, [uid]);

	// load recent received tips (last 3) via collectionGroup on transactions
	useEffect(() => {
		if (!uid) return () => {};
		const q = query(
			collection(db, 'transactions'),
			where('receiverId', '==', uid),
			where('status', '==', 'confirmed')
		);
		const unsub = onSnapshot(
			q,
			async (snap) => {
				const entries = snap.docs
					.map((d) => ({ id: d.id, ...d.data() }))
					.sort((a, b) => {
						const aTime = a.timestamp?.toMillis?.() || a.createdAt?.toMillis?.() || 0;
						const bTime = b.timestamp?.toMillis?.() || b.createdAt?.toMillis?.() || 0;
						return bTime - aTime;
					})
					.slice(0, 3);
				const enrichedEntries = await Promise.all(entries.map(async (entry) => {
					if (!entry.senderId) return entry;
					const sender = await getUserById(entry.senderId);
					return {
						...entry,
						senderName: sender?.name || sender?.displayName || sender?.fullName || sender?.email || entry.senderName || 'Supporter',
					};
				}));
				setRecentReceivedTips(enrichedEntries);
			},
			(err) => {
				console.error('Recent received tips snapshot error', err);
				setRecentReceivedTips([]);
			}
		);
		return () => unsub();
	}, [uid]);

    // Observe wallet balance realtime and glow on change
    useEffect(() => {
        if (!uid || !isOwnProfile) {
            setWalletBalance(0);
            return () => {};
        }
        let prev = null;
        const unsub = observeWalletBalance(uid, (bal) => {
            setWalletBalance(bal || 0);
            if (prev !== null && bal !== prev) {
                setWalletGlow(true);
                setTimeout(() => setWalletGlow(false), 2000);
            }
            prev = bal || 0;
        });
        return () => unsub();
    }, [uid, isOwnProfile]);

    // Real-time listeners for followers and following counts
    useEffect(() => {
        if (!uid) return () => {};
        
        // Listen to followers subcollection
        const followersCol = collection(db, "users", uid, "followers");
        const unsubFollowers = onSnapshot(followersCol, (snap) => {
            setFollowersCount(snap.size);
        }, (err) => {
            console.error('Error listening to followers:', err);
        });

        // Listen to following subcollection
        const followingCol = collection(db, "users", uid, "following");
        const unsubFollowing = onSnapshot(followingCol, (snap) => {
            setFollowingCount(snap.size);
        }, (err) => {
            console.error('Error listening to following:', err);
        });

        return () => {
            unsubFollowers();
            unsubFollowing();
        };
    }, [uid]);

    // Show skeleton immediately while data loads
    if (!user) {
        return (
            <div className="min-h-screen bg-[#0d0d0d] flex flex-col items-center justify-start">
                <div className="w-full max-w-md mx-auto px-4 overflow-hidden text-white">
                    <div className="flex flex-col items-center mt-8">
                        <div className="h-24 w-24 rounded-full bg-neutral-800 animate-pulse"></div>
                        <div className="h-8 w-48 bg-neutral-800 rounded mt-4 animate-pulse"></div>
                        <div className="h-12 w-full bg-neutral-800 rounded mt-6 animate-pulse"></div>
                    </div>
                </div>
            </div>
        );
    }

    // compute badge counts from titles array
    const badgeCounts = artistBadges.reduce((acc, t) => {
        if (!t) return acc;
        acc[t] = (acc[t] || 0) + 1;
        return acc;
    }, {});

    const badgesDisplay = [
        { title: 'Crown Fan', emoji: '👑' },
        { title: 'Diamond Fan', emoji: '💎' },
        { title: 'Gold Fan', emoji: '🥇' },
        { title: 'Silver Fan', emoji: '🥈' },
        { title: 'Bronze Fan', emoji: '🥉' },
    ];

    return (
        <div className="min-h-screen bg-[#0d0d0d] flex flex-col items-center justify-start">
            {/* Hidden file input for direct file picker */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".mp3,.aac,.m4a,.wav,.flac,.ogg,.opus,.aiff,audio/mpeg,audio/mp4,audio/x-m4a,audio/wav,audio/flac,audio/ogg,audio/opus,audio/aiff"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                disabled={uploading}
            />
            {/* Uploading overlay */}
            {uploading && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
                    <div className="bg-[#1a1a1a] rounded-2xl p-6 max-w-md w-full mx-4 text-center">
                        <div className="text-4xl mb-4">🎵</div>
                        <h3 className="text-xl font-bold text-white mb-2">Uploading Song...</h3>
                        <p className="text-white/70">Please wait while we upload your song</p>
                        {uploadError && (
                            <div className="mt-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
                                <p className="text-red-400 text-sm">{uploadError}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div className="w-full max-w-md mx-auto px-4 overflow-hidden text-white">
                {/* Avatar + Name */}
                <div className="mt-2 flex flex-col items-center">
                    <div className="relative">
                        <button
                            type="button"
                            disabled={!isOwnProfile}
                            onClick={() => window.dispatchEvent(new CustomEvent('open-profile-photo-editor'))}
                            aria-label="Edit profile picture"
                            className="h-24 w-24 rounded-full overflow-hidden bg-neutral-800 ring-2 ring-neutral-700 shadow-lg flex items-center justify-center border-0 p-0 disabled:cursor-default"
                        >
                            {user.photoURL ? (
                                <img src={user.photoURL} alt={user.name || 'Artist'} className="w-full h-full object-cover" />
                            ) : (
                                <div className="text-4xl font-bold text-white">{(user.name || user.displayName || 'A').charAt(0)}</div>
                            )}
                        </button>
                        {isOwnProfile && (
                            <button
                                onClick={() => window.dispatchEvent(new CustomEvent('open-profile-photo-editor'))}
                                title="Change photo"
                                className="absolute -bottom-1 -right-1 h-9 w-9 rounded-full bg-white border-2 border-[#0e0e0e] flex items-center justify-center text-black hover:scale-105 transition cursor-pointer"
                            >
                                📷
                            </button>
                        )}
                    </div>
                    <div className="mt-4 text-3xl font-extrabold tracking-wide text-white">
                        {user.name || user.displayName || 'Unknown Artist'}
                    </div>

                    {/* Stats */}
                    <div className="mt-5 grid w-full grid-cols-3 divide-x divide-neutral-800 rounded-2xl bg-[rgba(255,255,255,0.03)] py-3">
                        <div 
                            className="flex flex-col items-center cursor-pointer hover:opacity-80 transition"
                            onClick={() => setShowFollowingModal(true)}
                        >
                            <div className="text-2xl font-extrabold">{followingCount}</div>
                            <div className="mt-1 text-sm text-white/80">Following</div>
                        </div>
                        <div 
                            className="flex flex-col items-center cursor-pointer hover:opacity-80 transition"
                            onClick={() => setShowFollowersModal(true)}
                        >
                            <div className="text-2xl font-extrabold">{followersCount}</div>
                            <div className="mt-1 text-sm text-white/80">Followers</div>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="text-2xl font-extrabold">{(songs || []).length}</div>
                            <div className="mt-1 text-sm text-white/80">Posts</div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-5 flex w-full items-center justify-center gap-3">
                        {isOwnProfile ? (
                            <>
                                <button
                                    onClick={() => window.dispatchEvent(new CustomEvent('open-profile-editor'))}
                                    className="flex-1 max-w-[52%] select-none rounded-full bg-gradient-to-br from-[#ff3b3b] to-[#ff0000] px-5 py-3 text-base font-bold text-white shadow-[0_6px_20px_rgba(255,0,0,0.35)] ring-1 ring-red-500/30 active:scale-[0.98] transition"
                                >
                                    <span className="inline-flex items-center gap-2">👤 Edit Profile</span>
                                </button>
                                <button
                                    onClick={async () => { try { const link = `${window.location.origin}/profile/${uid}`; await navigator.clipboard.writeText(link); alert('Profile link copied'); } catch {} }}
                                    className="flex-1 max-w-[48%] select-none rounded-full bg-white px-5 py-3 text-base font-semibold text-black shadow-[0_6px_20px_rgba(0,0,0,0.25)] active:scale-[0.98] transition"
                                >
                                    <span className="inline-flex items-center gap-2">🔗 share profile</span>
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="min-w-0 flex-1">
                                    <FollowButton targetUid={uid} />
                                </div>
                                <button
                                    onClick={() => navigate(`/messages?to=${uid}`)}
                                    className="min-w-0 flex-1 rounded-full bg-white px-5 py-3 text-base font-semibold text-black shadow-[0_6px_20px_rgba(0,0,0,0.25)] active:scale-[0.98] transition"
                                >
                                    msg
                                </button>
                            </>
                        )}
                    </div>

                    {/* Wallet balance (fixed top-right) */}
                    {isOwnProfile && (
                        <div className="fixed top-4 right-4 z-40">
                            <div className={`px-4 py-2 rounded-full bg-white/10 ring-1 ring-white/20 text-white font-semibold shadow ${walletGlow ? 'animate-pulse' : ''}`}>
                                Wallet: ₹{Number(walletBalance || 0).toLocaleString('en-IN')}
                            </div>
                        </div>
                    )}

                    {/* Badges compact row */}
                    <div className="mt-6 grid w-full grid-cols-5 gap-1 px-1 sm:gap-3 sm:px-2">
                        {badgesDisplay.map(b => {
                            const badgeType = b.title.split(' ')[0].toLowerCase();
                            return (
                            <button
                                type="button"
                                key={b.title}
                                onClick={() => openBadgeFans(badgeType)}
                                className="flex min-w-0 items-center justify-center gap-1 rounded-xl px-1 py-2 text-white hover:bg-white/10 active:scale-95 transition sm:gap-2 sm:px-2"
                                title={`See ${b.title} fans`}
                            >
                                <span className="text-xl sm:text-2xl">{b.emoji}</span>
                                <span className="text-base font-semibold sm:text-lg">{artistBadgeCounts[badgeType] || 0}</span>
                            </button>
                            );
                        })}
                    </div>

                    {selectedBadgeType && (
                        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/75 p-0 sm:items-center sm:p-4" onClick={() => setSelectedBadgeType(null)}>
                            <div className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-3xl bg-[#141414] text-white ring-1 ring-white/15 sm:max-h-[85vh] sm:rounded-2xl" onClick={(event) => event.stopPropagation()}>
                                <div className="flex items-center justify-between border-b border-[#333] p-4 sm:p-5">
                                    <h3 className="text-lg font-bold capitalize">{selectedBadgeType} Badge Supporters</h3>
                                    <button type="button" onClick={() => setSelectedBadgeType(null)} className="text-2xl text-white/70 hover:text-white">×</button>
                                </div>
                                {loadingBadgeFans ? (
                                    <div className="flex items-center justify-center py-12 text-white/60">Loading supporters...</div>
                                ) : selectedBadgeFans.length === 0 ? (
                                    <div className="flex items-center justify-center py-12 text-white/60">No supporters have unlocked this badge yet.</div>
                                ) : (
                                    <div className="flex-1 space-y-3 overflow-y-auto p-4 sm:p-5">
                                        {selectedBadgeFans.map((fan) => (
                                            <div
                                                key={fan.id}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => { setSelectedBadgeType(null); navigate(`/profile/${fan.id}`); }}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        setSelectedBadgeType(null);
                                                        navigate(`/profile/${fan.id}`);
                                                    }
                                                }}
                                                className="flex cursor-pointer items-center gap-3 rounded-2xl border border-[#2a2a2a] bg-[#1c1c1c] p-3 shadow-md transition hover:border-red-500/40 hover:bg-[#222] sm:gap-4 sm:p-4"
                                            >
                                                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full bg-[#2a2a2a] ring-2 ring-red-500/20 sm:h-16 sm:w-16">
                                                    {fan.photoURL ? (
                                                        <img src={fan.photoURL} alt={fan.name} className="h-full w-full object-cover" />
                                                    ) : (
                                                        <div className="flex h-full w-full items-center justify-center text-xl font-bold sm:text-2xl">{fan.name.charAt(0).toUpperCase()}</div>
                                                    )}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span className="truncate text-base font-semibold sm:text-lg">{fan.name}</span>
                                                        {fan.role === 'artist' ? (
                                                            <span className="rounded-full bg-red-500 px-2 py-1 text-xs font-semibold">🎤 Artist</span>
                                                        ) : (
                                                            <span className="text-sm text-yellow-300">{BADGE_EMOJIS[selectedBadgeType]} {selectedBadgeType} Supporter</span>
                                                        )}
                                                    </div>
                                                    <div className="mt-1 text-xs text-white/55">{fan.tipCount} total tips</div>
                                                </div>
                                                <span className="shrink-0 text-white/40">›</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Recent tips */}
                    <div className="mt-7 w-full">
                        <div className="text-lg font-semibold tracking-wide mb-3">💰 Last 3 Tips</div>
                        <div className="space-y-2">
                            {recentReceivedTips.length === 0 ? (
                                <div className="text-white/50 text-center py-4">No tips yet</div>
                            ) : (
                                recentReceivedTips.map(t => (
                                    <div key={t.id} className="flex items-center justify-between rounded-2xl bg-white/5 px-4 py-3 ring-1 ring-white/10">
                                        <div className="flex min-w-0 items-center gap-3 flex-1">
                                            <div className="h-8 w-8 shrink-0 rounded-full bg-neutral-800 flex items-center justify-center text-sm font-bold">
                                                {(t.fromName?.[0] || 'S').toUpperCase()}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate font-medium text-white">
                                                    {t.senderName || t.fromName || 'Supporter'}
                                                    {t.badgeType && (
                                                        <span className="ml-2 text-xs text-yellow-300">
                                                            {t.badgeType === 'crown' ? '👑' : t.badgeType === 'diamond' ? '💎' : t.badgeType === 'gold' ? '🥇' : t.badgeType === 'silver' ? '🥈' : '🥉'} {t.badgeTitle || `${t.badgeType} badge`}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-white/60 truncate flex items-center gap-2">
                                                    <span>{new Date(t.timestamp?.toDate?.() || t.timestamp || Date.now()).toLocaleString()}</span>
                                                    {t.status === 'pending' && (
                                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-500 text-black">⚠️ Pending</span>
                                                    )}
                                                    {t.status === 'confirmed' && (
                                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500 text-white">💎 Confirmed</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="ml-3 shrink-0 text-right font-semibold text-white">
                                            {BADGE_EMOJIS[String(t.badgeType || '').toLowerCase()] || '🏅'} {Number(t.amount || 0)}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Posts Section - Instagram Style */}
                    <div className="mt-7 w-full">
                        <div className="text-lg font-semibold tracking-wide mb-3">Posts</div>
                        {songs.length === 0 ? (
                            <div className="text-white/50 text-center py-8">
                                No posts yet. Click the ➕ button in the header to post your first song!
                            </div>
                        ) : (
                            <div className="grid grid-cols-3 gap-1">
                                {/* Song posts */}
                                {songs.map(song => (
                                    <div
                                        key={song.id}
                                        className="aspect-square bg-white/5 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition relative group"
                                        onClick={() => {
                                            if (song.id) navigate(`/song/${song.id}`);
                                        }}
                                    >
                                        {song.coverImage || song.thumbnail ? (
                                            <img 
                                                src={song.coverImage || song.thumbnail} 
                                                alt={song.title || song.name} 
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-neutral-800 to-neutral-900">
                                                <span className="text-4xl">🎵</span>
                                            </div>
                                        )}
                                        {/* Overlay on hover */}
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="h-8 w-8">
                                                <path fillRule="evenodd" d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Upload Details Page Modal */}
            {showDetailsPage && selectedFile && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                    <div className="bg-[#1a1a1a] rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-[#1a1a1a] border-b border-white/10 px-6 py-4 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-white">Song Details</h2>
                            <button
                                onClick={() => {
                                    setShowDetailsPage(false);
                                    setSelectedFile(null);
                                    setSongName('');
                                    setConfirmedUpload(false);
                                }}
                                className="text-white/60 hover:text-white transition"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="p-6">
                            {uploadError && (
                                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
                                    <p className="text-red-400 text-sm">{uploadError}</p>
                                </div>
                            )}

                            {/* Selected File Info */}
                            <div className="mb-6 p-4 bg-[#111] rounded-xl">
                                <p className="text-white font-medium mb-1">Selected: {selectedFile.name}</p>
                                <p className="text-white/60 text-sm">
                                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                                </p>
                            </div>

                            {/* Song Name Input */}
                            <div className="mb-6">
                                <label htmlFor="song-name" className="block text-white mb-2 font-medium">
                                    Song Name
                                </label>
                                <input
                                    type="text"
                                    id="song-name"
                                    value={songName}
                                    onChange={(e) => setSongName(e.target.value)}
                                    placeholder="Enter song name"
                                    className="w-full px-4 py-3 bg-[#111] border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-white/30 transition"
                                />
                            </div>

                            {/* Upload Progress */}
                            {uploading && (
                                <div className="mb-6">
                                    <div className="w-full bg-[#111] rounded-full h-2.5 mb-2">
                                        <div 
                                            className="bg-gradient-to-r from-[#ff3b3b] to-[#ff0000] h-2.5 rounded-full transition-all duration-300"
                                            style={{ width: `${uploadProgress}%` }}
                                        ></div>
                                    </div>
                                    <p className="text-white/70 text-sm text-center">
                                        Uploading... {uploadProgress}%
                                    </p>
                                </div>
                            )}

                            {/* Music rights confirmation */}
                            <div className="mb-6 flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    id="confirm-upload"
                                    checked={confirmedUpload}
                                    onChange={(e) => setConfirmedUpload(e.target.checked)}
                                    className="mt-1 w-5 h-5 cursor-pointer accent-[#ff3b3b]"
                                    disabled={uploading}
                                />
                                <label 
                                    htmlFor="confirm-upload" 
                                    className="text-white cursor-pointer flex-1 leading-relaxed"
                                >
                                    I confirm that I own or have the necessary rights and permissions to upload and make this music available on HubTune. I understand that I am responsible for the content and rights associated with this upload.
                                    <Link to="/music-rights" className="mt-2 block text-red-400 underline hover:text-red-300">
                                        View Music Rights &amp; Content Policy
                                    </Link>
                                </label>
                            </div>

                            {/* Upload Button */}
                            <button
                                onClick={handleUploadFromDetails}
                                disabled={!songName.trim() || !confirmedUpload || uploading}
                                className={`w-full py-3 px-4 rounded-xl font-semibold transition ${
                                    !songName.trim() || !confirmedUpload || uploading
                                        ? 'bg-white/10 text-white/40 cursor-not-allowed'
                                        : 'bg-gradient-to-br from-[#ff3b3b] to-[#ff0000] text-white hover:from-[#ff4b4b] hover:to-[#ff1111] shadow-lg'
                                }`}
                            >
                                {uploading ? 'Uploading...' : 'Upload Song'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Modal */}
            {showUpload && user && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                    <div className="bg-[#1a1a1a] rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-[#1a1a1a] border-b border-white/10 px-6 py-4 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-white">Upload Song</h2>
                            <button
                                onClick={() => setShowUpload(false)}
                                className="text-white/60 hover:text-white transition"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="p-6">
                            <ArtistUpload 
                                user={user}
                                uid={uid}
                                onComplete={() => {
                                    setShowUpload(false);
                                    // Refresh songs list
                                    window.location.reload();
                                }} 
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Followers/Following Modals */}
            <FollowersFollowingModal
                isOpen={showFollowersModal}
                onClose={() => setShowFollowersModal(false)}
                userId={uid}
                type="followers"
                userData={user}
            />
            <FollowersFollowingModal
                isOpen={showFollowingModal}
                onClose={() => setShowFollowingModal(false)}
                userId={uid}
                type="following"
                userData={user}
            />
        </div>
    );
};


export default ArtistProfile;
